import React from 'react';
import './AtsScoreResult.css';

const AtsScoreResult = ({ show, onClose, result, onCreateResume }) => {
  if (!show || !result) return null;

  const { score, feedback, resume_text } = result;

  // Determine score color and grade
  const getScoreGrade = (score) => {
    if (score >= 90) return { grade: 'Excellent', color: '#10b981', emoji: '🌟' };
    if (score >= 75) return { grade: 'Good', color: '#3b82f6', emoji: '✅' };
    if (score >= 60) return { grade: 'Fair', color: '#f59e0b', emoji: '⚠️' };
    return { grade: 'Needs Improvement', color: '#ef4444', emoji: '❌' };
  };

  const scoreInfo = getScoreGrade(score);

  // Generate improvement suggestions based on score
  const getImprovements = () => {
    const improvements = [];
    
    if (score < 50) {
      improvements.push({
        icon: '🎯',
        title: 'Critical: ATS Compatibility',
        description: 'Your resume has significant formatting or keyword issues that prevent it from passing ATS systems.',
        priority: 'high'
      });
    }
    
    if (score < 70) {
      improvements.push({
        icon: '🔑',
        title: 'Add Relevant Keywords',
        description: 'Include industry-specific keywords and technical skills that match job descriptions.',
        priority: 'high'
      });
      improvements.push({
        icon: '📊',
        title: 'Quantify Achievements',
        description: 'Use numbers and metrics to demonstrate impact (e.g., "Increased sales by 30%").',
        priority: 'medium'
      });
    }
    
    if (score < 85) {
      improvements.push({
        icon: '✨',
        title: 'Optimize Formatting',
        description: 'Use simple, clean formatting with clear section headers and bullet points.',
        priority: 'medium'
      });
      improvements.push({
        icon: '💼',
        title: 'Strengthen Experience Section',
        description: 'Focus on accomplishments rather than responsibilities. Use action verbs.',
        priority: 'medium'
      });
    }
    
    improvements.push({
      icon: '📝',
      title: 'Tailor to Job Description',
      description: 'Customize your resume for each application by matching keywords from the job posting.',
      priority: 'low'
    });
    
    if (score >= 85) {
      improvements.push({
        icon: '🎓',
        title: 'Consider Adding Certifications',
        description: 'Include relevant professional certifications to strengthen your profile.',
        priority: 'low'
      });
    }
    
    return improvements;
  };

  const improvements = getImprovements();

  const handleCreateResume = () => {
    onClose();
    if (onCreateResume) {
      onCreateResume();
    }
  };

  return (
    <div className="ats-result-overlay" onClick={onClose}>
      <div className="ats-result-content" onClick={(e) => e.stopPropagation()}>
        <button className="ats-result-close" onClick={onClose}>×</button>
        
        <div className="ats-result-split">
          {/* Left Side - Improvements */}
          <div className="ats-left-panel">
            <div className="panel-header">
              <h2>
                <span className="header-icon">💡</span>
                Improvement Suggestions
              </h2>
              <p className="header-subtitle">Here's how to boost your ATS score</p>
            </div>

            <div className="improvements-list">
              {improvements.map((improvement, index) => (
                <div 
                  key={index} 
                  className={`improvement-card priority-${improvement.priority}`}
                >
                  <div className="improvement-icon">{improvement.icon}</div>
                  <div className="improvement-content">
                    <h4>{improvement.title}</h4>
                    <p>{improvement.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Feedback */}
            <div className="ai-feedback-box">
              <div className="feedback-label">
                <span className="feedback-icon">🤖</span>
                AI Analysis
              </div>
              <p>{feedback}</p>
            </div>

            {/* CTA for low scores */}
            {score < 50 && (
              <div className="low-score-cta">
                <div className="cta-content">
                  <h3>Need Help Creating a Professional Resume?</h3>
                  <p>Let our AI-powered resume builder create an ATS-optimized resume for you!</p>
                  <button className="btn-create-resume" onClick={handleCreateResume}>
                    <span className="btn-icon">✨</span>
                    Create Professional Resume
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Score */}
          <div className="ats-right-panel">
            <div className="score-section">
              <h3 className="score-title">Your ATS Score</h3>
              
              <div className="score-display">
                <div 
                  className="score-circle-large" 
                  style={{ 
                    '--score-color': scoreInfo.color,
                    '--score-percent': `${score}%` 
                  }}
                >
                  <svg className="score-ring" viewBox="0 0 200 200">
                    <circle 
                      className="score-ring-bg" 
                      cx="100" 
                      cy="100" 
                      r="85"
                    />
                    <circle 
                      className="score-ring-progress" 
                      cx="100" 
                      cy="100" 
                      r="85"
                      style={{
                        stroke: scoreInfo.color,
                        strokeDashoffset: `calc(534 - (534 * ${score}) / 100)`
                      }}
                    />
                  </svg>
                  
                  <div className="score-inner">
                    <div className="score-emoji">{scoreInfo.emoji}</div>
                    <div className="score-number" style={{ color: scoreInfo.color }}>
                      {score}
                    </div>
                    <div className="score-label">out of 100</div>
                  </div>
                </div>

                <div className="score-grade-badge" style={{ backgroundColor: scoreInfo.color }}>
                  {scoreInfo.grade}
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="score-breakdown">
                <h4>What This Means</h4>
                {score >= 90 && (
                  <p>🎉 Excellent! Your resume is highly optimized for ATS systems and should pass most automated screenings.</p>
                )}
                {score >= 75 && score < 90 && (
                  <p>👍 Good job! Your resume has strong ATS compatibility with room for minor improvements.</p>
                )}
                {score >= 60 && score < 75 && (
                  <p>⚠️ Fair score. Your resume needs some optimization to improve chances of passing ATS filters.</p>
                )}
                {score < 60 && (
                  <p>❌ Your resume needs significant improvements to pass ATS systems effectively.</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="quick-stats">
                <div className="stat-item">
                  <div className="stat-icon">📝</div>
                  <div className="stat-content">
                    <div className="stat-value">{resume_text?.experience?.length || 0}</div>
                    <div className="stat-label">Experience Entries</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">🎓</div>
                  <div className="stat-content">
                    <div className="stat-value">{resume_text?.education?.length || 0}</div>
                    <div className="stat-label">Education Entries</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">🛠️</div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {resume_text?.skills ? Object.keys(resume_text.skills).length : 0}
                    </div>
                    <div className="stat-label">Skill Categories</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="score-actions">
                {score >= 50 && (
                  <button className="btn-edit-resume" onClick={handleCreateResume}>
                    <span className="btn-icon">✏️</span>
                    Edit Resume
                  </button>
                )}
                <button className="btn-download-report">
                  <span className="btn-icon">📥</span>
                  Download Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtsScoreResult;
